<template>
  <div class="TargetRangeDonutChart">
    <svg
      class="TargetRangeDonutChart__svg"
      style="transform: rotate(-90deg);"
      :viewBox="`0 0 ${viewBoxSize} ${viewBoxSize}`"
    >
      <defs>
        <mask
          v-if="targetRange"
          :id="maskId"
        >
          <circle
            stroke="white"
            stroke-linecap="butt"
            :r="radius"
            :cx="center"
            :cy="center"
            :stroke-width="strokeWidth"
            :stroke-dasharray="`${targetRangeArcLength} ${circumference - targetRangeArcLength}`"
            :stroke-dashoffset="targetRangeDashOffset"
          />

          <circle
            stroke="black"
            stroke-linecap="butt"
            fill="none"
            :r="textBackgroundClipRadius"
            :cx="center"
            :cy="center"
            :stroke-width="textBackgroundClipWidth"
            :stroke-dasharray="`${textBackgroundClipArcLength} ${textBackgroundClipCircumference - textBackgroundClipArcLength}`"
            :stroke-dashoffset="textBackgroundDashOffset"
          />
        </mask>

        <pattern
          :id="patternId"
          patternUnits="userSpaceOnUse"
          width="5"
          height="5"
          patternTransform="rotate(45) scale(0.25)"
          stroke="var(--colorSwissBlack)"
        >
          <path d="M0,2.5 L5,2.5" />
        </pattern>
      </defs>

      <circle
        fill="transparent"
        stroke="var(--themeColorBackgroundOffset)"
        :r="radius"
        :cx="center"
        :cy="center"
        :stroke-width="strokeWidth"
      />

      <circle
        v-if="modelValue > 0"
        stroke="var(--colorSwissPurple300)"
        stroke-linecap="butt"
        fill="transparent"
        data-testid="donutFill"
        :r="radius"
        :cx="center"
        :cy="center"
        :stroke-width="strokeWidth"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="dashoffset"
      />

      <circle
        stroke="none"
        :mask="`url(#${maskId})`"
        :fill="`url(#${patternId})`"
        :cx="center"
        :cy="center"
        :r="viewBoxSize / 2"
      />

      <template v-if="targetRange">
        <path
          :id="textPathId"
          fill="none"
          :d="`
            M ${center - textPathRadius}, ${center}
            a ${textPathRadius},${textPathRadius} 0 1,1 ${viewBoxSize - textPathOffset},0
            a ${textPathRadius},${textPathRadius} 0 1,1 -${viewBoxSize - textPathOffset},0
          `"
        />

        <text
          class="TargetRangeDonutChart__targetLabel"
          font-size="3px"
          font-weight="570"
        >
          <textPath
            :href="`#${textPathId}`"
            :startOffset="textPathStartOffset"
          >
            Target range
          </textPath>
        </text>
      </template>
    </svg>

    <div class="TargetRangeDonutChart__labelContainer">
      <UIText
        weight="Medium"
        size="3XLarge"
      >
        {{ formatPercent(modelValue) }}
      </UIText>

      <UIText
        v-if="valueSuffix"
        weight="Medium"
        size="XSmall"
      >
        {{ valueSuffix }}
      </UIText>
    </div>
  </div>
</template>

<script lang="ts" setup>
import UIText from "@cosine/components/UIText.vue";
import formatPercent from "@cosine/lib/utils/financial/formatPercent";
import { computed, useId } from "vue";

const modelValue = defineModel<number>({
  required: true,
});

const {
  targetRange,
  valueSuffix,
} = defineProps<{
  targetRange?: [number, number],
  valueSuffix?: string,
}>();

const patternId = `targetRangeDonutChartPattern-${useId()}`;
const textPathId = `textPath-${useId()}`;
const maskId = `mask-${useId()}`;

const viewBoxSize = 100;
const strokeWidth = 20;
const center = computed(() => viewBoxSize / 2);

const radius = computed(() => (viewBoxSize - strokeWidth) / 2);
const circumference = computed(() => 2 * Math.PI * radius.value);
const dashoffset = computed(() => circumference.value - Math.min(modelValue.value, 1) * circumference.value);

const targetRangeArcLength = computed(() => {
  if (!targetRange) return 0;

  const [start, end] = targetRange;
  return (end - start) * circumference.value;
});

const targetRangeDashOffset = computed(() => {
  if (!targetRange) return 0;

  const [start] = targetRange;
  return circumference.value * (1 - start);
});

const textPathOffset = 7;
const textPathRadius = (viewBoxSize - textPathOffset) / 2;

const textPathStartOffset = computed(() => {
  if (!targetRange) return "0%";

  const start = targetRange[0];
  const offset = ((50 + (start * 100)) % 100);
  return `${offset}%`;
});

const textBackgroundClipWidth = 5;
const textBackgroundClipRadius = radius.value + strokeWidth / 2 - textBackgroundClipWidth / 2;
const textBackgroundClipCircumference = 2 * Math.PI * textBackgroundClipRadius;

const textBackgroundClipArcLength = computed(() => {
  if (!targetRange) return 0;

  const [start] = targetRange;
  const end = start + 0.068;
  return (end - start) * textBackgroundClipCircumference;
});

const textBackgroundDashOffset = computed(() => {
  if (!targetRange) return 0;

  const [start] = targetRange;
  return textBackgroundClipCircumference * (1 - start);
});
</script>

<style lang="scss" scoped>
.TargetRangeDonutChart {
  --patternColor: var(--colorSwissBlack);
  position: relative;
  display: flex;
  width: 100%;
}

.TargetRangeDonutChart__svg {
  width: 100%;
  max-width: 450px;
  aspect-ratio: 1;
  margin: 0 auto;
}

.TargetRangeDonutChart__targetLabel {
  font-family: var(--fontFamilySaans);
}

.TargetRangeDonutChart__labelContainer {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: grid;

  text-align: center;
}
</style>
